import { OnboardingDialogStep } from "@components/utils/OnboardingDialog/constants";
import { closeOnboardingDialog } from "@components/utils/OnboardingDialog/utils";
import { Paths } from "@constants/paths";
import { PostLoginInfoType } from "@types";
import { hideGameSearchModal } from "@views/HomePage/components/GameSearchModal/utils";
import { AuthToken } from "kz-ui-sdk";
import { useCallback } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useAuthSession from "./useAuthSession";
import usePostLoginInfo from "./usePostLoginInfo";

interface UsePostLoginHandlerProps {
  flowMode: "full-page" | "dialog";
  /**
   * flow mode:
   * - full-page: use react-router-dom navigate to navigate to the next step
   * - dialog: use navigate step to navigate to the next step, require registerFlowConfigs.navigateStep
   */
  registerFlowConfigs?: {
    navigateStep?: (step: OnboardingDialogStep) => void;
  };
}

/**
 * Pre-defined handler for post-login flow
 * Process authentication flow after login
 * Based on the onboarding mode, the handler will be different
 * - DIALOG_* (drawer or page): Use navigate step to navigate to the next step
 * - FULL_PAGE: Use react-router-dom navigate to navigate to the next step
 *
 * Process steps:
 * - Call to handleLoginSuccess or handleLoginSuccessWithToast to handle the login success
 * - Call to handlePostLoginCheckPassed to handle the post-login check passed
 * - Call to handlePostLoginCheckFailed to handle the post-login check failed
 *
 * Toast handler:
 * - Login flow -> show toast
 * - Forgot flow -> show toast
 * - Register flow -> Post-login check passed -> show toast
 * -              \-> Post-login check failed -> no toast
 */
export const usePostLoginHandler = ({ flowMode, registerFlowConfigs }: UsePostLoginHandlerProps) => {
  const { t } = useTranslation();
  const { type: postLoginType } = usePostLoginInfo();
  const navigatePage = useNavigate();
  const { isAuthenticated } = useAuthSession();

  const completeLoginHandler = useCallback(() => {
    switch (flowMode) {
      case "full-page":
        break;
      case "dialog":
        hideGameSearchModal();
        closeOnboardingDialog();
        break;
    }
  }, [flowMode]);

  const displayLoginSuccessMessage = useCallback(() => {
    if (isAuthenticated) {
      toast.success(t("Login successfully!"));
    }
  }, [t, isAuthenticated]);

  const handlePostLoginCheckPassed = useCallback(() => {
    displayLoginSuccessMessage();
    completeLoginHandler();
  }, [completeLoginHandler, displayLoginSuccessMessage]);

  const handlePostLoginCheckFailed = useCallback(() => {
    switch (flowMode) {
      case "dialog":
        if (!registerFlowConfigs?.navigateStep) return;
        switch (postLoginType) {
          case PostLoginInfoType.BANK_ACCOUNT:
            registerFlowConfigs.navigateStep(OnboardingDialogStep.AddBankAccount);
            break;
          case PostLoginInfoType.LEGAL_NAME:
            registerFlowConfigs.navigateStep(OnboardingDialogStep.AddLegalName);
            break;
          case PostLoginInfoType.BANK_LEGAL_NAME:
            registerFlowConfigs.navigateStep(OnboardingDialogStep.AddBankLegalName);
            break;
        }
        break;
      case "full-page":
        switch (postLoginType) {
          case PostLoginInfoType.BANK_ACCOUNT:
            navigatePage(Paths.PRIVATE.AUTH_BANK_ACCOUNT_PAGE, { replace: true });
            break;
          case PostLoginInfoType.LEGAL_NAME:
            navigatePage(Paths.PRIVATE.AUTH_LEGAL_NAME_PAGE, { replace: true });
            break;
          case PostLoginInfoType.BANK_LEGAL_NAME:
            navigatePage(Paths.PRIVATE.AUTH_BANK_LEGAL_NAME_PAGE, { replace: true });
            break;
        }
        break;
    }
  }, [postLoginType, registerFlowConfigs, navigatePage, flowMode]);

  const handleAuthSuccessOnLoginFlow = useCallback(
    (token?: AuthToken) => {
      if (!token) return;
      displayLoginSuccessMessage();
      completeLoginHandler();
    },
    [displayLoginSuccessMessage, completeLoginHandler],
  );

  const handleAuthSuccessOnForgotFlow = useCallback(
    (token?: AuthToken) => {
      if (!token) return;
      displayLoginSuccessMessage();
      completeLoginHandler();
    },
    [displayLoginSuccessMessage, completeLoginHandler],
  );

  const handleAuthSuccessOnResetFlow = useCallback(
    (token?: AuthToken) => {
      if (!token) return;
      completeLoginHandler();
    },
    [completeLoginHandler],
  );

  const handleAuthSuccessOnRegisterFlow = useCallback((token?: AuthToken) => {
    if (!token) return;
    hideGameSearchModal();
  }, []);

  return {
    loginFlowHandlers: {
      handleLoginSuccess: handleAuthSuccessOnLoginFlow,
    },
    forgotFlowHandlers: {
      handleLoginSuccess: handleAuthSuccessOnForgotFlow,
    },
    resetFlowHandlers: {
      handleLoginSuccess: handleAuthSuccessOnResetFlow,
    },
    registerFlowHandlers: {
      handleLoginSuccess: handleAuthSuccessOnRegisterFlow,
      handlePostLoginCheckPassed,
      handlePostLoginCheckFailed,
    },
  };
};
